/**
 * Audit Trail Page
 * Phase 2.2: Compliance-grade activity logging with filters and detail view
 * Professional audit interface for regulatory compliance
 */
import React, { useState, useEffect } from 'react';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from '../../../hooks/useVesselCollections';
import { useLicense } from '../../../hooks/useLicense';
import { getAuditTrail, getAuditStatistics, AUDIT_ACTIONS } from '../../../services/auditTrail';
import { AuditStorageHealth } from '../../admin/AuditStorageHealth';
import {
  Search,
  Filter,
  FileText,
  User,
  Calendar,
  BarChart,
  AlertTriangle
} from 'lucide-react';

const ACTION_LABELS = {
  [AUDIT_ACTIONS.CREATE]: 'Create',
  [AUDIT_ACTIONS.UPDATE]: 'Update',
  [AUDIT_ACTIONS.DELETE]: 'Delete',
  [AUDIT_ACTIONS.APPLY_REQUIREMENTS]: 'Apply Requirements',
  [AUDIT_ACTIONS.EXPORT]: 'Export',
  [AUDIT_ACTIONS.IMPORT]: 'Import',
  [AUDIT_ACTIONS.SYNC_PUSH]: 'Sync Push',
  [AUDIT_ACTIONS.SYNC_PULL]: 'Sync Pull',
  [AUDIT_ACTIONS.SYNC_CONFLICT]: 'Sync Conflict',
  [AUDIT_ACTIONS.LICENSE_VERIFY]: 'License Verify',
  [AUDIT_ACTIONS.LICENSE_EXPIRE]: 'License Expire',
  [AUDIT_ACTIONS.USER_INVITE]: 'User Invite',
  [AUDIT_ACTIONS.USER_ACTIVATE]: 'User Activate',
  [AUDIT_ACTIONS.USER_DEACTIVATE]: 'User Deactivate',
  [AUDIT_ACTIONS.LOGIN]: 'Login',
  [AUDIT_ACTIONS.LOGOUT]: 'Logout'
};

const ACTION_COLORS = {
  [AUDIT_ACTIONS.CREATE]: 'text-green-600 bg-green-50',
  [AUDIT_ACTIONS.UPDATE]: 'text-blue-600 bg-blue-50',
  [AUDIT_ACTIONS.DELETE]: 'text-red-600 bg-red-50',
  [AUDIT_ACTIONS.APPLY_REQUIREMENTS]: 'text-purple-600 bg-purple-50',
  [AUDIT_ACTIONS.EXPORT]: 'text-cyan-600 bg-cyan-50',
  [AUDIT_ACTIONS.IMPORT]: 'text-orange-600 bg-orange-50',
  [AUDIT_ACTIONS.SYNC_PUSH]: 'text-cyan-600 bg-cyan-50',
  [AUDIT_ACTIONS.SYNC_PULL]: 'text-teal-600 bg-teal-50',
  [AUDIT_ACTIONS.SYNC_CONFLICT]: 'text-red-600 bg-red-50',
  [AUDIT_ACTIONS.LICENSE_VERIFY]: 'text-emerald-600 bg-emerald-50',
  [AUDIT_ACTIONS.LICENSE_EXPIRE]: 'text-red-600 bg-red-50',
  [AUDIT_ACTIONS.USER_INVITE]: 'text-blue-600 bg-blue-50',
  [AUDIT_ACTIONS.USER_ACTIVATE]: 'text-green-600 bg-green-50',
  [AUDIT_ACTIONS.USER_DEACTIVATE]: 'text-gray-600 bg-gray-50',
  [AUDIT_ACTIONS.LOGIN]: 'text-green-600 bg-green-50',
  [AUDIT_ACTIONS.LOGOUT]: 'text-gray-600 bg-gray-50'
};

export const AuditTrailPage = () => {
  // Phase 2.6: Collections disabled - will restore in Phase 3
  // const { collections } = useVesselCollections();
  const { hasFeature, FEATURES } = useLicense();
  
  const [auditRecords, setAuditRecords] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    vesselId: '',
    action: '',
    collection: '',
    actor: '',
    startDate: '',
    endDate: '',
    limit: 100
  });

  // Phase 2.6: Audit data loading temporarily disabled - will restore in Phase 3
  // Load audit data
  useEffect(() => {
    console.warn('Phase 2.6: Audit trail disabled - will return in Phase 3');
    setAuditRecords([]);
    setStatistics({});
    setLoading(false);
  }, [filters]);

  const loadAuditData = async () => {
    console.warn('Phase 2.6: Audit trail disabled - will return in Phase 3');
    setLoading(false);
    return;

    // setLoading(true);
    // try {
    //   // Convert date strings to Date objects for API
    //   const apiFilters = { ...filters };
    //   if (apiFilters.startDate) {
    //     apiFilters.startDate = new Date(apiFilters.startDate);
    //   }
    //   if (apiFilters.endDate) {
    //     apiFilters.endDate = new Date(apiFilters.endDate);
    //   }
    //
    //   const [records, stats] = await Promise.all([
    //     getAuditTrail(collections, apiFilters),
    //     getAuditStatistics(collections, apiFilters)
    //   ]);
    //
    //   setAuditRecords(records);
    //   setStatistics(stats);
    // } catch (error) {
    //
    // } finally {
    //   setLoading(false);
    // }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      vesselId: '',
      action: '',
      collection: '',
      actor: '',
      startDate: '',
      endDate: '',
      limit: 100
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionBadge = (action) => {
    const label = ACTION_LABELS[action] || action;
    const colorClass = ACTION_COLORS[action] || 'text-gray-600 bg-gray-50';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {label}
      </span>
    );
  };

  const getCollectionLabel = (collection) => {
    const labels = {
      equipment: 'Equipment',
      procedures: 'Procedures',
      drills: 'Drills',
      training: 'Training',
      inspections: 'Inspections',
      maintenance: 'Maintenance',
      crew: 'Crew',
      incidents: 'Incidents',
      defects: 'Defects',
      hoursOfRest: 'Hours of Rest',
      activityLog: 'Activity Log',
      meta: 'System'
    };
    
    return labels[collection] || collection;
  };

  if (!hasFeature(FEATURES.AUDIT_TRAIL)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Audit Trail Not Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Audit trail requires a Fleet plan for compliance and regulatory requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Storage Health Warning */}
      <AuditStorageHealth />

      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Audit Trail</h3>
              <p className="mt-1 text-sm text-gray-500">
                Compliance-grade activity logging for regulatory requirements
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <BarChart className="h-4 w-4 mr-2" />
                Statistics
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Panel */}
        {showStatistics && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FileText className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Events
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {statistics.totalEvents || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Active Users
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {Object.keys(statistics.actors || {}).length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Calendar className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Date Range
                        </dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {statistics.timeRange?.oldest 
                            ? `${Math.ceil((statistics.timeRange.newest - statistics.timeRange.oldest) / (1000 * 60 * 60 * 24))} days`
                            : 'No data'
                          }
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Search className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Top Action
                        </dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {Object.entries(statistics.actions || {})
                            .sort(([,a], [,b]) => b - a)[0]?.[0] || 'None'
                          }
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Action</label>
                <select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                >
                  <option value="">All Actions</option>
                  {Object.entries(ACTION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Collection</label>
                <select
                  value={filters.collection}
                  onChange={(e) => handleFilterChange('collection', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                >
                  <option value="">All Collections</option>
                  <option value="equipment">Equipment</option>
                  <option value="procedures">Procedures</option>
                  <option value="drills">Drills</option>
                  <option value="training">Training</option>
                  <option value="inspections">Inspections</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="crew">Crew</option>
                  <option value="incidents">Incidents</option>
                  <option value="defects">Defects</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Limit</label>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                >
                  <option value={50}>50 records</option>
                  <option value={100}>100 records</option>
                  <option value={500}>500 records</option>
                  <option value={1000}>1000 records</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Audit Records Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Collection
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Record
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                    Loading audit records...
                  </td>
                </tr>
              ) : auditRecords.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                    No audit records found
                  </td>
                </tr>
              ) : (
                auditRecords.map((record) => (
                  <tr
                    key={record.id}
                    onClick={() => setSelectedRecord(record)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(record.ts)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.actor?.email || record.actor?.userId || 'System'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getActionBadge(record.action)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.collection ? getCollectionLabel(record.collection) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                      {record.recordId || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {record.details ? Object.keys(record.details).join(', ') : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Record Detail Modal */}
      {selectedRecord && (
        <AuditRecordModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
};

// Audit Record Detail Modal
const AuditRecordModal = ({ record, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Audit Record Details
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Timestamp</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(record.ts).toLocaleString()}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500">Action</label>
                  <p className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[record.action] || 'text-gray-600 bg-gray-50'}`}>
                      {ACTION_LABELS[record.action] || record.action}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Actor</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {record.actor?.email || record.actor?.userId || 'System'}
                  </p>
                  {record.actor?.device && (
                    <p className="text-xs text-gray-500">{record.actor.device}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Organization</label>
                  <p className="mt-1 text-sm text-gray-900">{record.orgId}</p>
                </div>

                {record.vesselId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Vessel</label>
                    <p className="mt-1 text-sm text-gray-900">{record.vesselId}</p>
                  </div>
                )}

                {record.collection && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Collection</label>
                    <p className="mt-1 text-sm text-gray-900">{record.collection}</p>
                  </div>
                )}

                {record.recordId && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Record ID</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono break-all">{record.recordId}</p>
                  </div>
                )}

                {(record.beforeHash || record.afterHash) && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Data Hashes</label>
                    <div className="mt-1 space-y-1">
                      {record.beforeHash && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Before:</span> {record.beforeHash}
                        </p>
                      )}
                      {record.afterHash && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">After:</span> {record.afterHash}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {record.details && Object.keys(record.details).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Details</label>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(record.details, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailPage;